import { useCallback, useEffect, useState } from "react";
import {
  Plus,
  Trash2,
  Pencil,
  Lightbulb,
  PackageCheck,
  Settings2,
  Truck,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Page, PageHeader, Empty } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { RECORDED_SHORTAGES_ENABLED } from "@/lib/features";

const PRIORITY_STYLES = {
  Urgent: "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300",
  Normal: "bg-muted text-muted-foreground",
  Low: "bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300",
};
const BLANK = {
  name: "",
  quantity: 1,
  units: "",
  supplier: "",
  est_cost: "",
  priority: "Normal",
  notes: "",
  inventory_id: null,
  category: "Equipment",
};

function ItemDialog({
  item,
  inventory,
  categories,
  onClose,
  onSaved,
  onManageCategories,
}) {
  const [f, setF] = useState({
    ...BLANK,
    ...item,
    category: categories.includes(item.category) ? item.category : categories[0] || BLANK.category,
    est_cost: item.est_cost ?? "",
  });
  const [inventoryQuery, setInventoryQuery] = useState(() => (
    inventory.find((inventoryItem) => inventoryItem.id === item.inventory_id)?.name || ""
  ));
  const [inventoryFocused, setInventoryFocused] = useState(false);
  const set = (k) => (v) => setF((o) => ({ ...o, [k]: v }));
  const suggestions = inventoryFocused && inventoryQuery.trim().length >= 2
    ? inventory.filter((inventoryItem) => (
      inventoryItem.name.toLowerCase().includes(inventoryQuery.trim().toLowerCase())
    )).slice(0, 6)
    : [];
  const linkInv = (inventoryItem) => {
    setInventoryQuery(inventoryItem.name);
    setInventoryFocused(false);
    setF((current) => ({
      ...current,
      inventory_id: inventoryItem.id,
      name: current.name || inventoryItem.name,
      units: current.units || inventoryItem.units || "",
      supplier: current.supplier || inventoryItem.supplier || "",
    }));
  };
  const save = async () => {
    const body = { ...f, quantity: Number(f.quantity), est_cost: f.est_cost === "" ? null : Number(f.est_cost) };
    try {
      item.id ? await api.put(`/tobuy/${item.id}`, body) : await api.post("/tobuy", body);
      toast.success(item.id ? "Item updated" : `${f.name} added to the To buy list`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="tobuy-dialog">
        <DialogHeader><DialogTitle>{item.id ? "Edit item" : "Add to the To buy list"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1">
            <Label>Link to inventory item (optional)</Label>
            <Input
              value={inventoryQuery}
              onChange={(event) => {
                setInventoryQuery(event.target.value);
                set("inventory_id")(null);
              }}
              onFocus={() => setInventoryFocused(true)}
              onBlur={() => setInventoryFocused(false)}
              placeholder="Start typing an inventory item…"
              data-testid="tobuy-inventory-search"
            />
            {suggestions.length > 0 && (
              <div
                className="max-h-44 overflow-y-auto rounded-md border bg-popover"
                data-testid="tobuy-inventory-suggestions"
              >
                {suggestions.map((inventoryItem) => (
                  <button
                    key={inventoryItem.id}
                    type="button"
                    className="block w-full border-b px-3 py-2 text-left text-sm last:border-0 hover:bg-muted"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => linkInv(inventoryItem)}
                    data-testid={`tobuy-inventory-suggestion-${inventoryItem.id}`}
                  >
                    {inventoryItem.name}
                  </button>
                ))}
              </div>
            )}
            {f.inventory_id && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto px-0 text-xs"
                onClick={() => {
                  setInventoryQuery("");
                  set("inventory_id")(null);
                }}
                data-testid="tobuy-inventory-clear-link"
              >
                Clear inventory link
              </Button>
            )}
          </div>
          <div className="col-span-2 space-y-1"><Label>Item</Label><Input value={f.name} onChange={(e) => set("name")(e.target.value)} data-testid="tobuy-name-input" /></div>
          <div className="space-y-1"><Label>Quantity</Label><Input type="number" min={1} value={f.quantity} onChange={(e) => set("quantity")(e.target.value)} data-testid="tobuy-quantity-input" /></div>
          <div className="space-y-1"><Label>Units</Label><Input value={f.units} placeholder="e.g. packs, litres" onChange={(e) => set("units")(e.target.value)} data-testid="tobuy-units-input" /></div>
          <div className="space-y-1"><Label>Supplier</Label><Input value={f.supplier} onChange={(e) => set("supplier")(e.target.value)} data-testid="tobuy-supplier-input" /></div>
          <div className="space-y-1"><Label>Estimated cost (£)</Label><Input type="number" min={0} step="0.01" value={f.est_cost} onChange={(e) => set("est_cost")(e.target.value)} data-testid="tobuy-cost-input" /></div>
          <div className="col-span-2 space-y-1"><div className="flex items-center justify-between gap-2"><Label>Category</Label><Button type="button" variant="ghost" size="sm" className="h-auto px-0 text-xs" onClick={onManageCategories} data-testid="tobuy-manage-categories-inline-button">Manage categories</Button></div>
            <Select
              value={categories.includes(f.category) ? f.category : categories[0] || "Equipment"}
              onValueChange={set("category")}
            >
              <SelectTrigger data-testid="tobuy-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((category) => <SelectItem key={category} value={category}>{category}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1"><Label>Priority</Label>
            <Select value={f.priority} onValueChange={set("priority")}>
              <SelectTrigger data-testid="tobuy-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["Urgent", "Normal", "Low"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1"><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => set("notes")(e.target.value)} data-testid="tobuy-notes-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.name.trim() || !(Number(f.quantity) > 0)} data-testid="tobuy-save-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceiveDialog({ item, onClose, onDone }) {
  const [addStock, setAddStock] = useState(!!item.inventory_id);
  const go = async () => {
    try {
      const { data } = await api.post(`/tobuy/${item.id}/status`, { status: "Received", add_to_stock: addStock });
      toast.success(data.stock_message || `${item.name} marked as received`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="tobuy-receive-dialog">
        <DialogHeader><DialogTitle>Mark {item.name} as received</DialogTitle></DialogHeader>
        {item.inventory_id ? (
          <label className="flex items-start gap-2.5 text-sm">
            <Checkbox checked={addStock} onCheckedChange={(v) => setAddStock(!!v)} className="mt-0.5" data-testid="tobuy-add-stock-checkbox" />
            <span>Add {item.quantity} {item.units} to the recorded stock in Inventory</span>
          </label>
        ) : <p className="text-sm text-muted-foreground">This item isn't linked to an inventory record, so remember to add it to Inventory yourself.</p>}
        <DialogFooter><Button onClick={go} data-testid="tobuy-receive-confirm">Mark received</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ItemRow({ it, onEdit, onReceive, onStatus, onDelete }) {
  return (
    <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`tobuy-row-${it.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">{it.name}</span>
          <span className={`text-[11px] rounded-full px-2 py-0.5 ${PRIORITY_STYLES[it.priority]}`} data-testid={`tobuy-priority-${it.id}`}>{it.priority}</span>
          <span className="text-[11px] rounded-full bg-teal-100 px-2 py-0.5 text-teal-800" data-testid={`tobuy-category-${it.id}`}>{it.category || "Equipment"}</span>
          {it.source !== "manual" && <span className="text-[11px] text-muted-foreground">{{ low_stock: "from low stock", shortage: "from a shortage", jonny: "added via Jonny" }[it.source] || ""}</span>}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          <span className="font-semibold text-foreground">{it.quantity} {it.units}</span>
          {it.supplier && ` · ${it.supplier}`}{it.est_cost != null && ` · £${Number(it.est_cost).toFixed(2)}`} · added by {it.added_by}
        </div>
        {it.notes && <div className="text-xs mt-1">{it.notes}</div>}
      </div>
      <div className="flex items-center gap-1.5">
        {it.status === "Needed" && <Button size="sm" variant="outline" onClick={() => onStatus(it, "Ordered")} data-testid={`tobuy-mark-ordered-${it.id}`}><Truck className="h-3.5 w-3.5 mr-1.5" />Ordered</Button>}
        {it.status !== "Received" && <Button size="sm" onClick={() => onReceive(it)} data-testid={`tobuy-mark-received-${it.id}`}><PackageCheck className="h-3.5 w-3.5 mr-1.5" />Received</Button>}
        {it.status === "Ordered" && <Button size="sm" variant="ghost" onClick={() => onStatus(it, "Needed")} data-testid={`tobuy-mark-needed-${it.id}`}>Undo</Button>}
        {it.status !== "Received" && <Button size="icon" variant="ghost" onClick={() => onEdit(it)} data-testid={`tobuy-edit-${it.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
        <Button size="icon" variant="ghost" onClick={() => onDelete(it)} data-testid={`tobuy-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );
}

function CategoryManager({ onClose, onUpdated }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const load = useCallback(() => {
    setLoaded(false);
    api
      .get("/tobuy/categories/manage")
      .then((response) => setItems(response.data.categories))
      .catch((error) => toast.error(errMsg(error)))
      .finally(() => setLoaded(true));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const add = async () => {
    setBusy(true);
    try {
      await api.post("/tobuy/categories", { name });
      setName("");
      await load();
      onUpdated();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (category) => {
    try {
      await api.delete(`/tobuy/categories/${category.id}`);
      await load();
      onUpdated();
      toast.success(`${category.name} deleted`);
      setPendingDelete(null);
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  return (
    <>
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-h-[80vh] max-w-lg overflow-y-auto"
        data-testid="tobuy-category-manager-dialog"
      >
        <DialogHeader><DialogTitle>Manage To buy categories</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="New category"
            data-testid="tobuy-category-name-input"
          />
          <Button
            onClick={add}
            disabled={busy || !name.trim()}
            data-testid="tobuy-category-add-button"
          >
            Add
          </Button>
        </div>
        <div className="min-h-28 divide-y rounded-lg border">
          {!loaded && (
            <p className="p-3 text-sm text-muted-foreground" data-testid="tobuy-category-loading">
              Loading categories…
            </p>
          )}
          {loaded && items.map((category) => (
            <div key={category.name} className="flex items-center justify-between gap-3 p-3">
              <div>
                <p className="text-sm font-medium">{category.name}</p>
                <p className="text-xs text-muted-foreground">
                  {category.item_count} item{category.item_count === 1 ? "" : "s"}
                </p>
              </div>
              {category.can_delete ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPendingDelete(category)}
                  data-testid={`tobuy-category-delete-${category.id}`}
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  Delete
                </Button>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {category.delete_reason || "Keep one category"}
                </span>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
    <AlertDialog
      open={Boolean(pendingDelete)}
      onOpenChange={(open) => !open && setPendingDelete(null)}
    >
      <AlertDialogContent data-testid="tobuy-category-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {pendingDelete?.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This category is unused. Existing To buy items will not be changed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="tobuy-category-delete-cancel">
            Keep category
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => remove(pendingDelete)}
            data-testid="tobuy-category-delete-confirm"
          >
            Delete category
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

function Suggestions({ items, onAdd }) {
  return (
    <div className="rounded-xl border bg-card p-4" data-testid="tobuy-suggestions-panel">
      <h3 className="font-semibold text-sm flex items-center gap-2 mb-1"><Lightbulb className="h-4 w-4 text-amber-500" />Suggested</h3>
      <p className="text-xs text-muted-foreground mb-3">From low recorded stock.</p>
      {items.length === 0 ? <p className="text-xs text-muted-foreground">Nothing to suggest right now.</p> : (
        <div className="space-y-2">
          {items.map((s) => (
            <div key={s.key} className="flex items-start gap-2 text-sm border-b last:border-0 pb-2" data-testid={`tobuy-suggestion-${s.key}`}>
              <div className="flex-1 min-w-0"><div className="font-medium truncate">{s.name}</div><div className="text-[11px] text-muted-foreground">{s.reason}</div></div>
              <Button size="sm" variant="outline" className="h-7" onClick={() => onAdd(s)} data-testid={`tobuy-suggestion-add-${s.key}`}><Plus className="h-3 w-3 mr-1" />Add</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ToBuyPage() {
  const [items, setItems] = useState([]);
  const [sugg, setSugg] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [tab, setTab] = useState("Needed");
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState(["Equipment", "Chemicals", "Consumables"]);
  const [managingCategories, setManagingCategories] = useState(false);
  const [edit, setEdit] = useState(null);
  const [receive, setReceive] = useState(null);
  const load = useCallback(() => {
    api.get("/tobuy").then((r) => setItems(r.data));
    api.get("/tobuy/suggestions").then((r) => setSugg(r.data));
  }, []);
  const loadCategories = useCallback(() => {
    api.get("/tobuy/categories").then((response) => setCategories(response.data));
  }, []);
  useEffect(() => {
    load();
    loadCategories();
    api.get("/inventory").then((r) => setInventory(r.data)).catch(() => {});
  }, [load, loadCategories]);

  const setStatus = async (it, status) => {
    try {
      await api.post(`/tobuy/${it.id}/status`, { status });
      toast.success(`${it.name} marked as ${status.toLowerCase()}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async (it) => {
    await api.delete(`/tobuy/${it.id}`);
    toast.success(`${it.name} removed`);
    load();
  };
  const addSuggestion = (s) => setEdit({ name: s.name, quantity: s.quantity, units: s.units, supplier: s.supplier, inventory_id: s.inventory_id, source: s.source, notes: s.reason, priority: s.source === "shortage" ? "Urgent" : "Normal", category: s.category || "Equipment" });

  const visibleItems = RECORDED_SHORTAGES_ENABLED
    ? items
    : items.filter((item) => item.source !== "shortage");
  const counts = Object.fromEntries(["Needed", "Ordered", "Received"].map((s) => [s, visibleItems.filter((i) => i.status === s).length]));
  const ql = q.trim().toLowerCase();
  const order = { Urgent: 0, Normal: 1, Low: 2 };
  const shown = visibleItems.filter((i) => i.status === tab && (category === "all" || (i.category || "Equipment") === category) && (!ql || `${i.name} ${i.supplier} ${i.notes}`.toLowerCase().includes(ql)))
    .sort((a, b) => order[a.priority] - order[b.priority]);
  const total = shown.reduce((t, i) => t + (i.est_cost || 0), 0);

  return (
    <Page wide>
      <PageHeader title="To buy" subtitle="Equipment, chemicals and consumables the prep room needs to order.">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setManagingCategories(true)} data-testid="tobuy-manage-categories-button"><Settings2 className="h-4 w-4 mr-2" />Manage categories</Button>
          <Button onClick={() => setEdit({})} data-testid="tobuy-add-button"><Plus className="h-4 w-4 mr-2" />Add item</Button>
        </div>
      </PageHeader>
      <div className="grid lg:grid-cols-[1fr_300px] gap-6">
        <div className="space-y-4 min-w-0">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                {["Needed", "Ordered", "Received"].map((s) => <TabsTrigger key={s} value={s} data-testid={`tobuy-tab-${s.toLowerCase()}`}>{s} ({counts[s]})</TabsTrigger>)}
              </TabsList>
            </Tabs>
            <div className="flex flex-wrap gap-2">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-44" data-testid="tobuy-category-filter"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All categories</SelectItem>{categories.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Search items or suppliers…" value={q} onChange={(e) => setQ(e.target.value)} className="sm:w-64" data-testid="tobuy-search-input" />
            </div>
          </div>
          {shown.length === 0 ? <Empty>{tab === "Needed" ? "Nothing on the list. Add an item or use a suggestion." : `No ${tab.toLowerCase()} items.`}</Empty> : (
            <>
              <div className="rounded-xl border bg-card divide-y" data-testid="tobuy-list">
                {shown.map((it) => <ItemRow key={it.id} it={it} onEdit={setEdit} onReceive={setReceive} onStatus={setStatus} onDelete={del} />)}
              </div>
              {total > 0 && <p className="text-xs text-muted-foreground text-right" data-testid="tobuy-total">Estimated total: <span className="font-semibold text-foreground">£{total.toFixed(2)}</span></p>}
            </>
          )}
        </div>
        <aside><Suggestions items={sugg} onAdd={addSuggestion} /></aside>
      </div>
      {edit && (
        <ItemDialog
          item={edit}
          inventory={inventory}
          categories={categories}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            load();
          }}
          onManageCategories={() => setManagingCategories(true)}
        />
      )}
      {receive && <ReceiveDialog item={receive} onClose={() => setReceive(null)} onDone={() => { setReceive(null); load(); }} />}
      {managingCategories && <CategoryManager onClose={() => setManagingCategories(false)} onUpdated={loadCategories} />}
    </Page>
  );
}
